import { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft } from 'lucide-react-native';
import { PracticeQuestionCard } from '../components/business/PracticeQuestionCard';
import { AppButton } from '../components/ui/AppButton';
import { AppText } from '../components/ui/AppText';
import { showAppToast } from '../components/ui/AppToast';
import { tokens } from '../theme/tokens';
import type {
  PracticeMasteryStatus,
  PracticeQuestionViewModel,
} from '../types/wrongQuestions';

const demoPracticeQuestion: PracticeQuestionViewModel = {
  id: 'practice-linear-equation',
  title: '错题再练：一元一次方程',
  subject: '数学',
  knowledgePoint: '方程移项',
  sourceLabel: '高一期中考试',
  errorType: '移项漏变号',
  prompt: '解方程：2x + 3 = 13，求 x 的值。',
  options: [
    { id: 'a', label: 'A', content: 'x = 3' },
    { id: 'b', label: 'B', content: 'x = 5' },
    { id: 'c', label: 'C', content: 'x = 8' },
    { id: 'd', label: 'D', content: 'x = 10' },
  ],
  correctOptionId: 'b',
  correctAnswer: 'x = 5',
  explanation: '先将常数项 3 移到等号右侧，得到 2x = 10，再两边同时除以 2，所以 x = 5。',
};

export default function WrongQuestionPracticePage() {
  const router = useRouter();
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [masteryStatus, setMasteryStatus] = useState<PracticeMasteryStatus>('未掌握');

  const submitAnswer = useCallback(() => {
    if (!selectedOptionId) {
      showAppToast('请先选择一个答案', 'info');
      return;
    }

    setSubmitted(true);
    const nextStatus: PracticeMasteryStatus =
      selectedOptionId === demoPracticeQuestion.correctOptionId ? '已掌握' : '模糊';
    setMasteryStatus(nextStatus);
    showAppToast(nextStatus === '已掌握' ? '答对了，已更新掌握状态' : '已展示解析，建议再练一次', nextStatus === '已掌握' ? 'success' : 'info');
  }, [selectedOptionId]);

  const retryPractice = useCallback(() => {
    setSelectedOptionId(null);
    setSubmitted(false);
    setMasteryStatus('未掌握');
  }, []);

  const markMastery = useCallback((status: PracticeMasteryStatus) => {
    setMasteryStatus(status);
    showAppToast(`掌握状态已更新为：${status}`, 'success');
  }, []);

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="错题本"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
        <View style={styles.headerCopy}>
          <AppText variant="title" weight="bold">
            再练一次
          </AppText>
          <AppText variant="caption" tone="muted">
            本地答题、判分和掌握状态闭环
          </AppText>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <PracticeQuestionCard
          question={demoPracticeQuestion}
          selectedOptionId={selectedOptionId}
          submitted={submitted}
          masteryStatus={masteryStatus}
          onSelectOption={setSelectedOptionId}
          onSubmit={submitAnswer}
          onRetry={retryPractice}
          onMarkMastery={markMastery}
          onOpenPhotoSearch={() => router.push('/photo-search')}
          onBackToWrongBook={() => router.push('/wrong-questions')}
        />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
  },
});
